import imageCompression from 'browser-image-compression';

/**
 * Upper bound for a single logo/background upload. Mirrors `MAX_IMAGE_BYTES`
 * in andor-server (`src/routes/settings/uploadHelpers.ts`); the server rejects
 * the whole settings request with HTTP 400 when a file exceeds it.
 */
export const MAX_UPLOAD_IMAGE_BYTES = 5 * 1024 * 1024;

/** Raster images larger than this edge are downscaled even when small enough in bytes. */
const MAX_EDGE_PX = 4096;

export const ALLOWED_UPLOAD_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/svg+xml',
  'image/webp',
];

export class ImageTooLargeError extends Error {
  constructor(
    public readonly size: number,
    public readonly maxBytes: number
  ) {
    super(`Image is too large (${size} bytes, maximum ${maxBytes})`);
    this.name = 'ImageTooLargeError';
  }
}

/**
 * Returns `file` unchanged when it already fits `maxBytes`; otherwise re-encodes
 * / downscales a raster image (aspect ratio and MIME type preserved, so PNG/WebP
 * transparency survives) until it does. SVGs cannot be shrunk without losing the
 * vector, so an oversized SVG — like a raster image the encoder cannot get under
 * the limit — rejects with {@link ImageTooLargeError}.
 *
 * `browser-image-compression` treats `maxSizeMB` as MiB, the same unit the
 * server uses for its limit.
 */
export async function fitImageToUploadLimit(
  file: File,
  maxBytes = MAX_UPLOAD_IMAGE_BYTES
): Promise<File> {
  if (file.size <= maxBytes) {
    return file;
  }
  if (file.type === 'image/svg+xml') {
    throw new ImageTooLargeError(file.size, maxBytes);
  }

  // Deliberately on the main thread: the library's worker does not bundle
  // itself but `importScripts()` the lib from a CDN at runtime, which hangs
  // without network access (CI) and adds an external dependency to an
  // admin-only action that takes a few hundred milliseconds at most.
  const compressed = await imageCompression(file, {
    maxSizeMB: maxBytes / 1024 / 1024,
    maxWidthOrHeight: MAX_EDGE_PX,
    initialQuality: 0.9,
    useWebWorker: false,
  });
  if (compressed.size > maxBytes) {
    throw new ImageTooLargeError(compressed.size, maxBytes);
  }
  // The library may hand back a Blob with a `name` expando rather than a real
  // File; FormData would then send it as `filename="blob"`, and the server
  // derives the stored extension from that name. Always re-wrap it.
  return new File([compressed], file.name, {
    type: compressed.type,
    lastModified: file.lastModified,
  });
}

/** Reads a file as a `data:` URL — used for the instant local preview. */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('Could not read file'));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(Math.max(decimals, 0)))} ${sizes[i]}`;
}
